import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import s from './Financeiro.module.css';

const VAZIO = { nome: '', ativo: true, tipo: 'ambos', plano_conta_id: '' };

const TIPO_LABEL = { credito: 'Crédito', debito: 'Débito', ambos: 'Crédito e Débito' };

export default function BandeirasCartao() {
  const [bandeiras, setBandeiras] = useState([]);
  const [sel, setSel]             = useState(null);
  const [form, setForm]           = useState(VAZIO);
  const [taxas, setTaxas]         = useState([]); // [{parcelas, taxa}]
  const [modo, setModo]           = useState('lista');
  const [salvando, setSalvando]   = useState(false);
  const [erro, setErro]           = useState('');
  const [sucesso, setSucesso]     = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [planoContas, setPlanoContas] = useState([]);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get('/financeiro/bandeiras');
      setBandeiras(data);
    } catch { setErro('Erro ao carregar bandeiras.'); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    api.get('/financeiro/plano-contas/analiticas', { params: { tipo: 'despesa' } })
      .then(({ data }) => setPlanoContas(data))
      .catch(() => setPlanoContas([]));
  }, []);

  const bandFiltradas = bandeiras.filter(b => mostrarInativos || b.ativo);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const novo = () => {
    setForm(VAZIO);
    setTaxas([{ parcelas: 1, taxa: '0' }]);
    setSel(null); setErro(''); setSucesso(''); setModo('novo');
  };

  const editar = (b) => {
    setForm({ nome: b.nome, ativo: b.ativo, tipo: b.tipo || 'ambos', plano_conta_id: b.plano_conta_id || '' });
    setTaxas((b.taxas || []).map(t => ({ parcelas: t.parcelas, taxa: String(t.taxa) })));
    if ((b.taxas || []).length === 0) setTaxas([{ parcelas: 1, taxa: '0' }]);
    setSel(b); setErro(''); setSucesso(''); setModo('editar');
  };

  const cancelar = () => { setModo('lista'); setSel(null); setErro(''); setSucesso(''); };

  const addTaxa = () => {
    const maxParc = taxas.reduce((m, t) => Math.max(m, t.parcelas), 0);
    setTaxas(p => [...p, { parcelas: maxParc + 1, taxa: '0' }]);
  };

  const removeTaxa = (i) => setTaxas(p => p.filter((_, j) => j !== i));

  const setTaxa = (i, k, v) => setTaxas(p => p.map((t, j) => j === i ? { ...t, [k]: v } : t));

  const salvar = async () => {
    if (!form.nome.trim()) { setErro('Nome é obrigatório.'); return; }
    const taxasPayload = taxas
      .filter(t => t.parcelas > 0)
      .map(t => ({ parcelas: parseInt(t.parcelas), taxa: parseFloat(t.taxa) || 0 }));

    setErro(''); setSalvando(true);
    try {
      const payload = { ...form, plano_conta_id: form.plano_conta_id || null, taxas: taxasPayload };
      if (modo === 'novo') {
        await api.post('/financeiro/bandeiras', payload);
      } else {
        await api.put(`/financeiro/bandeiras/${sel.id}`, payload);
      }
      setSucesso('Salvo com sucesso!');
      setTimeout(() => setSucesso(''), 2500);
      await carregar();
      setModo('lista');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setSalvando(false); }
  };

  const fmtTaxa = (t) => `${parseFloat(t.taxa || 0).toFixed(2).replace('.', ',')}%`;

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>💳</span> Bandeiras de Cartão</div>

      <div className={s.toolbar}>
        <button className={s.btnErpPrimario} onClick={novo}>+ Nova Bandeira</button>
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={mostrarInativos} onChange={e => setMostrarInativos(e.target.checked)} />
          Mostrar inativas
        </label>
      </div>

      {erro    && <div className={s.erroMsg}>{erro}</div>}
      {sucesso && <div className={s.sucessoMsg}>{sucesso}</div>}

      <div className={s.conteudoSplit}>
        {/* Lista */}
        <div className={`${s.painel} ${s.painelLeft}`}>
          <div className={s.painelScroll}>
            {bandFiltradas.length === 0
              ? <div style={{ padding: 16, color: '#888', fontSize: 12 }}>Nenhuma bandeira cadastrada.</div>
              : bandFiltradas.map(b => (
                <div key={b.id}
                  className={`${s.listaItem} ${sel?.id === b.id ? s.listaItemSel : ''}`}
                  onClick={() => editar(b)}>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{b.nome}</div>
                    <div style={{ fontSize: 11, opacity: 0.75 }}>
                      {TIPO_LABEL[b.tipo] || 'Crédito e Débito'} — {(b.taxas || []).length} parcela(s) configurada(s)
                    </div>
                    {(b.taxas || []).length > 0 && (
                      <div style={{ fontSize: 11, opacity: 0.75 }}>
                        1x: {fmtTaxa(b.taxas[0])}
                        {b.taxas.length > 1 && ` | ${b.taxas.length}x: ${fmtTaxa(b.taxas[b.taxas.length-1])}`}
                      </div>
                    )}
                  </div>
                  <span className={`${s.badge} ${b.ativo ? s.badgeAtivo : s.badgeInativo}`}>
                    {b.ativo ? 'Ativa' : 'Inativa'}
                  </span>
                </div>
              ))
            }
          </div>
        </div>

        {/* Formulário */}
        <div className={s.painelRight}>
          {modo === 'lista'
            ? <div style={{ color: '#888', fontSize: 12, marginTop: 20, textAlign: 'center' }}>
                Selecione uma bandeira ou clique em "+ Nova Bandeira".
              </div>
            : (
              <div className={s.form}>
                <div className={s.fieldset}>
                  <div className={s.legend}>Dados da Bandeira</div>
                  <div className={s.formRow}>
                    <div className={s.formGrupo} style={{ flex: 1 }}>
                      <label className={s.label}>Nome *</label>
                      <input className={`${s.input} ${s.inputLarge}`} value={form.nome}
                        onChange={e => set('nome', e.target.value)} autoFocus />
                    </div>
                    <div className={s.formGrupo}>
                      <label className={s.label}>Tipo</label>
                      <select className={s.select} value={form.tipo}
                        onChange={e => set('tipo', e.target.value)}>
                        <option value="ambos">Crédito e Débito</option>
                        <option value="credito">Crédito</option>
                        <option value="debito">Débito</option>
                      </select>
                    </div>
                    {modo === 'editar' && (
                      <div className={s.formGrupo}>
                        <label className={s.label}>Status</label>
                        <select className={s.select} value={form.ativo ? '1' : '0'}
                          onChange={e => set('ativo', e.target.value === '1')}>
                          <option value="1">Ativa</option>
                          <option value="0">Inativa</option>
                        </select>
                      </div>
                    )}
                  </div>
                  <div className={s.formRow}>
                    <div className={s.formGrupo} style={{ flex: 1 }}>
                      <label className={s.label}>Conta do Plano de Contas (taxas na baixa)</label>
                      <select className={s.select} value={form.plano_conta_id}
                        onChange={e => set('plano_conta_id', e.target.value ? parseInt(e.target.value) : '')}>
                        <option value="">— Padrão (6.3.04 Taxas de Cartões) —</option>
                        {planoContas.map(p => (
                          <option key={p.id} value={p.id}>{p.codigo} — {p.descricao}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className={s.fieldset}>
                  <div className={s.legend}>Taxas por Parcela (%)</div>
                  <table className={s.tabela} style={{ marginBottom: 8 }}>
                    <thead>
                      <tr>
                        <th>Parcelas</th>
                        <th>Taxa (%)</th>
                        <th style={{ width: 32 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {taxas.map((t, i) => (
                        <tr key={i}>
                          <td>
                            <input className={s.input} type="number" min="1" max="24"
                              value={t.parcelas} style={{ width: 70 }}
                              onChange={e => setTaxa(i, 'parcelas', parseInt(e.target.value) || 1)} />
                          </td>
                          <td>
                            <input className={s.input} type="number" min="0" step="0.01"
                              value={t.taxa} style={{ width: 90 }}
                              onChange={e => setTaxa(i, 'taxa', e.target.value)} />
                          </td>
                          <td>
                            <button className={s.btnErp} style={{ minWidth: 26, padding: '0 4px', height: 22 }}
                              onClick={() => removeTaxa(i)}>✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button className={s.btnErp} onClick={addTaxa}>+ Parcela</button>
                  <div style={{ fontSize: 11, color: '#666', marginTop: 6 }}>
                    Configure uma linha para cada número de parcelas com sua taxa correspondente.
                  </div>
                </div>

                <div className={s.rodape} style={{ marginTop: 0, borderTop: 'none', paddingLeft: 0 }}>
                  <button className={s.btnErp} onClick={cancelar}>Cancelar</button>
                  <button className={s.btnErpPrimario} onClick={salvar} disabled={salvando}>
                    {salvando ? 'Salvando...' : '✔ Salvar'}
                  </button>
                </div>
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
}
